import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useCollaborationTypes } from '@/hooks/useCollaborationTypes';
import { useBrandManager } from '@/contexts/BrandProvider';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Briefcase, Globe, Twitter, Database, Sparkles, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CollaborationTypeDialog } from '@/components/collaborations/CollaborationTypeDialog';
import { EnrichmentDataDisplay } from '@/components/collaborations/EnrichmentDataDisplay';

interface CollaboratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  initialData?: any;
}

export const CollaboratorDialog = ({ open, onOpenChange, onSubmit, initialData }: CollaboratorDialogProps) => {
  const { currentBrand } = useBrandManager();
  const { types, createType, refetch: fetchTypes } = useCollaborationTypes(currentBrand?.id || null);
  const { toast } = useToast();
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [pendingOpportunityType, setPendingOpportunityType] = useState<string | null>(null);
  const { register, handleSubmit, setValue, watch, reset, getValues } = useForm({
    defaultValues: { 
      name: '', 
      email: '', 
      phone: '', 
      company: '',
      linkedin: '',
      status: 'non-eligible',
      collaboration_type_ids: [] as string[],
      insights: '',
      remarks: ''
    }
  });

  // Reset form when initialData changes or dialog opens
  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        company: initialData.company || '',
        linkedin: initialData.linkedin || '',
        status: initialData.status || 'non-eligible',
        collaboration_type_ids: initialData.collaboration_type_ids || [],
        insights: initialData.insights || '',
        remarks: initialData.remarks || ''
      });
    } else if (open) {
      reset({
        name: '',
        email: '',
        phone: '',
        company: '',
        linkedin: '',
        status: 'non-eligible',
        collaboration_type_ids: [],
        insights: '',
        remarks: ''
      });
    }
  }, [initialData, open, reset]);

  const status = watch('status');
  const collaborationTypeIds = watch('collaboration_type_ids');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={initialData ? "Edit Collaborator" : "Create Collaborator"}
            popupContext="/collaborators"
            isOpen={open}
          >
            {initialData ? 'Edit' : 'Add'} Collaborator
          </DialogTitleWithBug>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="w-full flex-1 flex flex-col min-h-0">
          <TabsList className="w-full bg-transparent p-0 h-auto border-b border-border">
            <div className={`grid w-full ${initialData ? 'grid-cols-3' : 'grid-cols-1'}`}>
              <TabsTrigger 
                value="details" 
                className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-4 py-2"
              >
                Basic Data
              </TabsTrigger>
              {initialData && (
                <>
                  <TabsTrigger 
                    value="enrichment"
                    className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-4 py-2"
                  >
                    Enrichment Data
                    {initialData?.last_enriched_at && (
                      <Badge variant="secondary" className="ml-2">Enriched</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="opportunities"
                    className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-4 py-2"
                  >
                    Collabs Opportunities
                    {initialData?.collaboration_opportunities && Array.isArray(initialData.collaboration_opportunities) && initialData.collaboration_opportunities.length > 0 && (
                      <Badge variant="secondary" className="ml-2">{initialData.collaboration_opportunities.length}</Badge>
                    )}
                  </TabsTrigger>
                </>
              )}
            </div>
          </TabsList>

          <TabsContent value="details" className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" {...register('name', { required: true })} />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" {...register('email', { required: true })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" {...register('phone')} />
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input id="linkedin" {...register('linkedin')} placeholder="https://linkedin.com/in/..." />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="collaboration_type_ids">Collaboration Types</Label>
                  <MultiSelect
                    options={types.map(type => ({ label: type.name, value: type.id }))}
                    selected={collaborationTypeIds || []}
                    onChange={(selected) => setValue('collaboration_type_ids', selected)}
                    placeholder="Select types..."
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={status} onValueChange={(value) => setValue('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non-eligible">Non-Eligible</SelectItem>
                      <SelectItem value="eligible">Eligible</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in progress">In Progress</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="company">Company</Label>
                <Input id="company" {...register('company')} />
              </div>

              <div>
                <Label htmlFor="insights">Insights</Label>
                <Textarea id="insights" {...register('insights')} placeholder="Any relevant insights or notes..." />
              </div>

              <div>
                <Label htmlFor="remarks">Remarks</Label>
                <Textarea id="remarks" {...register('remarks')} placeholder="Additional remarks..." />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button type="submit">{initialData ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </TabsContent>

          {initialData && (
            <TabsContent value="enrichment" className="flex-1 overflow-y-auto">
              <div className="space-y-4">
                {/* Enrichment Status */}
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-medium">Enrichment Status</span>
                  </div>
                  {initialData.last_enriched_at ? (
                    <Badge variant="default" className="gap-1">
                      <Calendar className="h-3 w-3" />
                      Last enriched: {new Date(initialData.last_enriched_at).toLocaleDateString()}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not enriched</Badge>
                  )}
                </div>

                {/* Enriched Fields */}
                <div className="grid grid-cols-2 gap-4">
                  {initialData.job_title && (
                    <div className="space-y-1">
                      <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Briefcase className="h-3 w-3" />
                        Job Title
                      </Label>
                      <div className="p-2 bg-muted rounded-md">{initialData.job_title}</div>
                    </div>
                  )}

                  {initialData.location && (
                    <div className="space-y-1">
                      <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        Location
                      </Label>
                      <div className="p-2 bg-muted rounded-md">{initialData.location}</div>
                    </div>
                  )}

                  {initialData.website && (
                    <div className="space-y-1">
                      <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Globe className="h-3 w-3" />
                        Website
                      </Label>
                      <div className="p-2 bg-muted rounded-md">
                        <a href={initialData.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {initialData.website}
                        </a>
                      </div>
                    </div>
                  )}

                  {initialData.twitter_handle && (
                    <div className="space-y-1">
                      <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Twitter className="h-3 w-3" />
                        Twitter Handle
                      </Label>
                      <div className="p-2 bg-muted rounded-md">@{initialData.twitter_handle}</div>
                    </div>
                  )}
                </div>

                {/* Enrichment Source */}
                {initialData.enrichment_source && (
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Database className="h-3 w-3" />
                      Enrichment Source
                    </Label>
                    <div className="p-2 bg-muted rounded-md">
                      <Badge variant="outline">{initialData.enrichment_source}</Badge>
                    </div>
                  </div>
                )}

                {/* Enrichment Data */}
                {initialData.enrichment_data && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Enrichment Data
                    </Label>
                    <EnrichmentDataDisplay enrichmentData={initialData.enrichment_data} />
                  </div>
                )}

                {!initialData.last_enriched_at && (
                  <div className="p-4 text-center text-muted-foreground border border-dashed rounded-lg">
                    <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No enrichment data available</p>
                    <p className="text-xs mt-1">Use "Enrich & Classify" to gather information about this collaborator</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {initialData && (
            <TabsContent value="opportunities" className="flex-1 overflow-y-auto">
              <div className="space-y-6 pr-2">
                {/* Your Collaboration Types Section */}
                {types.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-semibold">Your Collaboration Types</Label>
                      <Badge variant="secondary">{types.length}</Badge>
                    </div>
                    <div className="space-y-3">
                      {types.map(type => {
                        // Analyze this type against collaborator
                        const collaboratorProfile = {
                          name: initialData.name,
                          email: initialData.email,
                          company: initialData.company,
                          job_title: initialData.job_title,
                          location: initialData.location,
                          website: initialData.website,
                          linkedin: initialData.linkedin,
                          twitter_handle: initialData.twitter_handle,
                          enrichment_data: initialData.enrichment_data,
                          ai_opportunities: initialData.collaboration_opportunities || []
                        };

                        let score = 0;
                        let reasons: string[] = [];
                        let matchedOpportunity: any = null;

                        // Check if any AI-generated opportunity matches this type
                        const matchingOpp = collaboratorProfile.ai_opportunities.find((opp: any) => {
                          if (!opp.type) return false;
                          const typeName = type.name.toLowerCase();
                          const oppType = opp.type.toLowerCase();
                          return typeName.includes(oppType) || oppType.includes(typeName);
                        });

                        if (matchingOpp) {
                          score += matchingOpp.confidence || 5;
                          reasons.push(`AI analysis suggests this is a good fit (confidence: ${matchingOpp.confidence || 5}/10)`);
                          matchedOpportunity = matchingOpp;
                        }

                        // Deep analysis of type description
                        const typeDesc = (type.description || '').toLowerCase();
                        const typeName = type.name.toLowerCase();
                        
                        // Analyze based on job title and type description
                        if (collaboratorProfile.job_title) {
                          const jobTitle = collaboratorProfile.job_title.toLowerCase();
                          
                          // Check if job title keywords match type name or description
                          const jobTitleWords = jobTitle.split(/\s+/).filter(w => w.length > 2);
                          const typeWords = [...typeName.split(/\s+/), ...typeDesc.split(/\s+/)].filter(w => w.length > 2);
                          
                          const matchingWords = jobTitleWords.filter(word => 
                            typeWords.some(typeWord => 
                              typeWord.includes(word) || word.includes(typeWord)
                            )
                          );
                          
                          if (matchingWords.length > 0) {
                            score += 3;
                            reasons.push(`Job title (${collaboratorProfile.job_title}) aligns with collaboration type: "${type.name}"`);
                          }
                          
                          // Also check for common collaboration-related keywords
                          const relevantKeywords = ['content', 'marketing', 'seo', 'writer', 'blogger', 'influencer', 'expert', 'author', 'editor', 'journalist', 'creator', 'publisher', 'founder', 'ceo', 'director'];
                          const hasRelevantJob = relevantKeywords.some(keyword => 
                            jobTitle.includes(keyword) && (typeName.includes(keyword) || typeDesc.includes(keyword))
                          );
                          
                          if (hasRelevantJob) {
                            score += 2;
                            reasons.push(`Job title contains relevant keywords that match the collaboration type description`);
                          }
                        }
                        
                        // Analyze type description content for requirements, characteristics, and context
                        if (type.description) {
                          const collaboratorInfo = [
                            collaboratorProfile.job_title,
                            collaboratorProfile.company,
                            collaboratorProfile.location,
                            collaboratorProfile.name
                          ].filter(Boolean).join(' ').toLowerCase();
                          
                          // Check if description mentions things the collaborator has
                          if ((typeDesc.includes('website') || typeDesc.includes('web presence')) && collaboratorProfile.website) {
                            score += 2;
                            reasons.push(`Type description mentions website requirement, and collaborator has a website`);
                          }
                          if ((typeDesc.includes('linkedin') || typeDesc.includes('linkedin profile')) && collaboratorProfile.linkedin) {
                            score += 2;
                            reasons.push(`Type description mentions LinkedIn, and collaborator has a LinkedIn profile`);
                          }
                          if ((typeDesc.includes('social media') || typeDesc.includes('social presence') || typeDesc.includes('twitter')) && collaboratorProfile.twitter_handle) {
                            score += 2;
                            reasons.push(`Type description mentions social media, and collaborator has social presence`);
                          }
                          
                          // Check for industry/niche keywords in description
                          const industryKeywords = ['tech', 'saas', 'startup', 'agency', 'consulting', 'ecommerce', 'b2b', 'b2c', 'software', 'digital', 'online'];
                          const hasIndustryMatch = industryKeywords.some(keyword => 
                            typeDesc.includes(keyword) && collaboratorInfo.includes(keyword)
                          );
                          
                          if (hasIndustryMatch) {
                            score += 2;
                            reasons.push(`Type description mentions industry/niche that matches collaborator's profile`);
                          }
                          
                          // Check for collaboration-specific keywords in description
                          const collabKeywords = ['guest post', 'guest posting', 'article', 'blog', 'content', 'interview', 'podcast', 'webinar', 'workshop', 'event', 'speaking', 'expertise', 'knowledge'];
                          const hasCollabMatch = collabKeywords.some(keyword => 
                            typeDesc.includes(keyword) && (
                              collaboratorProfile.job_title?.toLowerCase().includes(keyword) ||
                              collaboratorProfile.company?.toLowerCase().includes(keyword) ||
                              typeName.includes(keyword)
                            )
                          );
                          
                          if (hasCollabMatch) {
                            score += 2;
                            reasons.push(`Type description mentions collaboration activities that align with collaborator's profile`);
                          }
                          
                          // Check if description mentions audience size, reach, or influence
                          if ((typeDesc.includes('audience') || typeDesc.includes('followers') || typeDesc.includes('reach')) && 
                              (collaboratorProfile.linkedin || collaboratorProfile.twitter_handle || collaboratorProfile.website)) {
                            score += 1;
                            reasons.push(`Type description mentions audience/reach requirements, and collaborator has online presence`);
                          }
                        }

                        // Analyze based on company
                        if (collaboratorProfile.company) {
                          score += 1;
                          reasons.push(`Has company affiliation: ${collaboratorProfile.company}`);
                        }

                        // Analyze based on location
                        if (collaboratorProfile.location) {
                          score += 1;
                          reasons.push(`Location: ${collaboratorProfile.location}`);
                        }

                        // Analyze based on prerequisites
                        if (type.prerequisites) {
                          const prerequisites = type.prerequisites.toLowerCase();
                          const hasWebsite = !!collaboratorProfile.website;
                          const hasLinkedIn = !!collaboratorProfile.linkedin;
                          const hasSocial = !!collaboratorProfile.twitter_handle;
                          
                          if (prerequisites.includes('website') && hasWebsite) {
                            score += 2;
                            reasons.push('Meets prerequisite: Has website');
                          }
                          if (prerequisites.includes('linkedin') && hasLinkedIn) {
                            score += 2;
                            reasons.push('Meets prerequisite: Has LinkedIn profile');
                          }
                          if (prerequisites.includes('social') && hasSocial) {
                            score += 2;
                            reasons.push('Meets prerequisite: Has social media presence');
                          }
                        }

                        const normalizedScore = Math.min(10, Math.round(score));
                        const fitLevel = normalizedScore >= 7 ? 'excellent' : normalizedScore >= 5 ? 'good' : normalizedScore >= 3 ? 'moderate' : 'low';

                        return (
                          <div key={type.id} className={`p-4 rounded-lg border-2 ${
                            fitLevel === 'excellent' ? 'bg-green-50 border-green-300' :
                            fitLevel === 'good' ? 'bg-blue-50 border-blue-200' :
                            fitLevel === 'moderate' ? 'bg-yellow-50 border-yellow-200' :
                            'bg-muted border-border'
                          }`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="default" className="bg-primary text-primary-foreground">
                                    {type.name}
                                  </Badge>
                                  <span className={`text-sm font-semibold ${
                                    fitLevel === 'excellent' ? 'text-green-700' :
                                    fitLevel === 'good' ? 'text-blue-700' :
                                    fitLevel === 'moderate' ? 'text-yellow-700' :
                                    'text-muted-foreground'
                                  }`}>
                                    Fit Score: {normalizedScore}/10
                                  </span>
                                </div>
                                {type.description && (
                                  <p className="text-sm text-foreground mb-2">{type.description}</p>
                                )}
                              </div>
                            </div>

                            {matchedOpportunity && (
                              <div className="mb-3 p-3 bg-white rounded border">
                                <p className="text-xs font-medium text-muted-foreground mb-1">AI Analysis:</p>
                                <p className="text-sm text-foreground">{matchedOpportunity.description}</p>
                                {matchedOpportunity.next_steps && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    <strong>Next Steps:</strong> {matchedOpportunity.next_steps}
                                  </p>
                                )}
                              </div>
                            )}

                            {reasons.length > 0 && (
                              <div className="mb-3 space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">Why this is a good fit:</p>
                                <ul className="text-xs text-foreground space-y-1">
                                  {reasons.map((reason, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                      <span className="mt-1">•</span>
                                      <span>{reason}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {type.prerequisites && (
                              <div className="mb-3 p-2 bg-white rounded border">
                                <p className="text-xs font-medium text-muted-foreground mb-1">Prerequisites:</p>
                                <p className="text-xs text-foreground">{type.prerequisites}</p>
                              </div>
                            )}

                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={async () => {
                                const currentTypes = collaborationTypeIds || [];
                                if (!currentTypes.includes(type.id)) {
                                  const updatedTypes = [...currentTypes, type.id];
                                  setValue('collaboration_type_ids', updatedTypes);
                                  
                                  // If editing an existing collaborator, save immediately
                                  if (initialData?.id) {
                                    const formData = getValues();
                                    try {
                                      await onSubmit({
                                        ...formData,
                                        collaboration_type_ids: updatedTypes
                                      });
                                      // Update initialData to reflect the change immediately
                                      if (initialData) {
                                        initialData.collaboration_type_ids = updatedTypes;
                                      }
                                    } catch (error) {
                                      toast({
                                        title: "Error",
                                        description: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
                                        variant: "destructive"
                                      });
                                      return; // Don't show success toast if save failed
                                    }
                                  }
                                  
                                  toast({
                                    title: "Added",
                                    description: `${type.name} has been added to this collaborator's collaboration types`
                                  });
                                } else {
                                  toast({
                                    title: "Already added",
                                    description: `${type.name} is already in this collaborator's collaboration types`,
                                    variant: "default"
                                  });
                                }
                              }}
                            >
                              Add this to collaborator
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* AI Suggested Opportunities Section */}
                {initialData.collaboration_opportunities && Array.isArray(initialData.collaboration_opportunities) && initialData.collaboration_opportunities.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-semibold">AI Suggested Opportunities</Label>
                      <Badge variant="secondary">{initialData.collaboration_opportunities.length}</Badge>
                    </div>
                    <div className="space-y-3">
                      {initialData.collaboration_opportunities.map((opp: any, index: number) => {
                        // Check if this opportunity matches any existing type
                        const matchingType = types.find(t => {
                          const typeName = t.name.toLowerCase();
                          const oppType = opp.type?.toLowerCase() || '';
                          return typeName.includes(oppType) || oppType.includes(typeName);
                        });

                        // Generate reasoning for this opportunity
                        const reasons: string[] = [];
                        if (opp.confidence) {
                          reasons.push(`AI confidence score: ${opp.confidence}/10`);
                        }
                        if (initialData.job_title) {
                          reasons.push(`Based on job title: ${initialData.job_title}`);
                        }
                        if (initialData.company) {
                          reasons.push(`Based on company: ${initialData.company}`);
                        }
                        if (matchingType) {
                          reasons.push(`Matches your collaboration type: ${matchingType.name}`);
                        }

                        return (
                          <div key={index} className={`p-4 rounded-lg border ${
                            matchingType ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
                          }`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant={matchingType ? "default" : "secondary"}>
                                    {opp.type || 'Opportunity'}
                                  </Badge>
                                  {matchingType && (
                                    <Badge variant="outline" className="bg-green-100 text-green-800">
                                      Matches: {matchingType.name}
                                    </Badge>
                                  )}
                                  {opp.confidence && (
                                    <span className="text-xs text-muted-foreground">
                                      Confidence: {opp.confidence}/10
                                    </span>
                                  )}
                                </div>
                                {opp.description && (
                                  <p className="text-sm text-foreground mb-2">{opp.description}</p>
                                )}
                              </div>
                            </div>

                            {reasons.length > 0 && (
                              <div className="mb-3 space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">Why this opportunity:</p>
                                <ul className="text-xs text-foreground space-y-1">
                                  {reasons.map((reason, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                      <span className="mt-1">•</span>
                                      <span>{reason}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {opp.next_steps && (
                              <div className="mb-3 p-2 bg-white rounded border">
                                <p className="text-xs font-medium text-muted-foreground mb-1">Next Steps:</p>
                                <p className="text-xs text-foreground">{opp.next_steps}</p>
                              </div>
                            )}

                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={async () => {
                                if (matchingType) {
                                  const currentTypes = collaborationTypeIds || [];
                                  if (!currentTypes.includes(matchingType.id)) {
                                    const updatedTypes = [...currentTypes, matchingType.id];
                                    setValue('collaboration_type_ids', updatedTypes);
                                    
                                    // If editing an existing collaborator, save immediately
                                    if (initialData?.id) {
                                      const formData = getValues();
                                      try {
                                        await onSubmit({
                                          ...formData,
                                          collaboration_type_ids: updatedTypes
                                        });
                                        // Update initialData to reflect the change immediately
                                        if (initialData) {
                                          initialData.collaboration_type_ids = updatedTypes;
                                        }
                                      } catch (error) {
                                        toast({
                                          title: "Error",
                                          description: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
                                          variant: "destructive"
                                        });
                                        return; // Don't show success toast if save failed
                                      }
                                    }
                                    
                                    toast({
                                      title: "Added",
                                      description: `${matchingType.name} has been added to this collaborator's collaboration types`
                                    });
                                  } else {
                                    toast({
                                      title: "Already added",
                                      description: `${matchingType.name} is already in this collaborator's collaboration types`,
                                      variant: "default"
                                    });
                                  }
                                } else {
                                  // Store the opportunity type name for creating a new type
                                  const oppTypeName = opp.type || 'New Collaboration Type';
                                  setPendingOpportunityType(oppTypeName);
                                  setIsTypeDialogOpen(true);
                                  toast({
                                    title: "No matching type found",
                                    description: `"${oppTypeName}" doesn't match any existing collaboration type. Creating a new type...`,
                                    variant: "default"
                                  });
                                }
                              }}
                            >
                              Add this to collaborator
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {types.length === 0 && (!initialData.collaboration_opportunities || initialData.collaboration_opportunities.length === 0) && (
                  <div className="p-4 text-center text-muted-foreground border border-dashed rounded-lg">
                    <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No collaboration types or opportunities available</p>
                    <p className="text-xs mt-1">Create collaboration types or use "Enrich & Classify" to generate opportunities</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
      
      {/* Collaboration Type Creation Dialog */}
      <CollaborationTypeDialog
        open={isTypeDialogOpen}
        onOpenChange={(open) => {
          setIsTypeDialogOpen(open);
          if (!open) {
            setPendingOpportunityType(null);
          }
        }}
        onSubmit={async (typeData) => {
          const newType = await createType({
            ...typeData,
            name: pendingOpportunityType || typeData.name
          });
          
          if (newType) {
            // Refresh types list
            await fetchTypes();
            
            // Automatically add the new type to the collaborator's collaboration_type_ids
            const currentTypes = collaborationTypeIds || [];
            if (!currentTypes.includes(newType.id)) {
              const updatedTypes = [...currentTypes, newType.id];
              setValue('collaboration_type_ids', updatedTypes);
              
              // If editing an existing collaborator, save immediately
              if (initialData?.id) {
                const formData = getValues();
                await onSubmit({
                  ...formData,
                  collaboration_type_ids: updatedTypes
                });
              }
              
              toast({
                title: "Type created and added",
                description: `${newType.name} has been created and added to this collaborator's collaboration types`
              });
            }
            
            setIsTypeDialogOpen(false);
            setPendingOpportunityType(null);
          }
        }}
        initialData={pendingOpportunityType ? {
          name: pendingOpportunityType,
          description: '',
          prerequisites: ''
        } : undefined}
      />
    </Dialog>
  );
};
